/**
 * What each docket item changes — turn `6a`, from the canvas.
 *
 * Set once, before the meeting; the vote then applies it. This is the front half of the mechanism
 * whose back half is the configuration table on Admin › Settings (`6b`), and the two are one
 * design: a rule passes in a room, and either the app writes a value for the season it names, or
 * it records honestly that it cannot and lists what has to be built first.
 *
 * FOUR KINDS, and the fourth is most of the rulebook. `records only` — team-name obligations,
 * trade etiquette, how the meeting is run — joins the rulebook, becomes citable, and changes no
 * value. Saying that plainly stops a commissioner hunting for a setting that was never going to
 * exist, which is the honest answer for the majority of the league's 141 rules.
 *
 * DECLARING IS NOT A GATE. An item with nothing declared can still be voted on and recorded; the
 * effect is a convenience. The counter in the header says how many are done rather than blocking
 * on the ones that are not, because a meeting starting on time matters more than a form being
 * complete.
 *
 * The effective season is on every item because THE OFFSEASON OVERLAPS THE VOTE. The committee
 * meets on 26 August: the farm auction is still ahead of it, so a farm change can take this
 * season, while free keepers cannot without re-pricing a slate a GM has already submitted.
 */

import { useMemo, useState } from 'react';
import {
  KIND_BLURB, KIND_LABEL, SETTING_LABEL, knockOn, seasonOptions,
  type EffectKind, type ProposalEffect, type SeasonFacts, type SettingKey,
} from '../domain/effects.js';
import { LEAGUE_2026 } from '../domain/config.js';
import { docketNumbers, thresholdNote } from '../domain/proposals.js';
import { managerShort } from '../domain/identity.js';
import { GmBadge } from './GmBadge.js';
import { Rule } from './RuleText.js';
import type { RuleProposal } from '../store/types.js';

const KINDS: EffectKind[] = ['parameter', 'table', 'build', 'records'];

/** The settings a commissioner may point a `parameter` effect at, in the order 6b lists them. */
const KEYS: SettingKey[] = [
  'farmPicksPerYear', 'freeKeepers', 'maxKeepers', 'draftRounds', 'rosterSize', 'maxFarmPlayers',
  'irSlots', 'faabBase', 'sleeperMinRound', 'regularSeasonWeeks',
];

export function DeclareEffect(
  { agenda, season, facts, onSave }: {
    agenda: readonly RuleProposal[];
    season: number;
    facts: SeasonFacts;
    onSave: (p: RuleProposal, effect: ProposalEffect | undefined) => void;
  },
) {
  const numbers = useMemo(
    () => docketNumbers(
      agenda.map((p) => p.id),
      new Map(agenda.map((p) => [p.id, { branchedFrom: p.branchedFrom }])),
    ),
    [agenda],
  );
  const declared = agenda.filter((p) => p.effect).length;

  if (agenda.length === 0) return null;

  return (
    <section className="card effects">
      <div className="striptop">
        <h3 className="striphead">What each item changes</h3>
        <span className="mut">
          {declared} of {agenda.length} declared
        </span>
      </div>
      <p className="note">
        Set once, before the meeting. The vote then applies it — an item with nothing declared can
        still be voted on.
      </p>

      <ul className="efflist">
        {agenda.map((p) => (
          <EffectCard key={p.id} p={p} number={numbers.get(p.id) ?? '?'} season={season}
            facts={facts} parentNumber={p.branchedFrom ? numbers.get(p.branchedFrom) : undefined}
            onSave={(e) => onSave(p, e)} />
        ))}
      </ul>

      <details className="kindlegend">
        <summary>Does this cover every proposal?</summary>
        <p className="note">Four kinds, and the fourth is most of the rulebook.</p>
        <dl>
          {KINDS.map((k) => (
            <div key={k}>
              <dt><span className={`chip kind ${k}`}>{KIND_LABEL[k]}</span></dt>
              <dd>{KIND_BLURB[k]}</dd>
            </div>
          ))}
        </dl>
      </details>
    </section>
  );
}

function EffectCard(
  { p, number, parentNumber, season, facts, onSave }: {
    p: RuleProposal; number: string; parentNumber?: string; season: number;
    facts: SeasonFacts; onSave: (e: ProposalEffect | undefined) => void;
  },
) {
  const [draft, setDraft] = useState<ProposalEffect | null>(p.effect ?? null);
  const [open, setOpen] = useState(false);
  const effect = draft ?? p.effect;

  const options = useMemo(
    () => seasonOptions(effect ?? { kind: 'records' }, season, facts),
    [effect, season, facts],
  );
  const knocks = useMemo(
    () => (effect ? knockOn(effect, LEAGUE_2026) : []),
    [effect],
  );

  if (!p.effect && !open) {
    return (
      <li className="effcard bare">
        <div className="effhead">
          <span className="slotno">{number}</span>
          <b>{p.title}</b>
        </div>
        {/*
          The undeclared state says what it costs — nothing. A commissioner reading "declare an
          effect" beside four filled cards will otherwise assume he is blocked.
        */}
        <p className="note">
          Nothing declared. It can still be voted on — the effect is a convenience, not a gate.
        </p>
        <button type="button" className="btn small" onClick={() => {
          setOpen(true);
          setDraft({ kind: 'parameter', season: season + 1, sets: [] });
        }}>declare an effect</button>
      </li>
    );
  }

  const e = effect!;
  const commit = (next: ProposalEffect) => { setDraft(next); onSave(next); };

  return (
    <li className={`effcard ${e.kind}`}>
      <div className="effhead">
        <span className="slotno">{number}</span>
        <b>{p.title}</b>
        <span className="effwho">
          <GmBadge gm={p.gm} />
          {(p.proposers ?? [p.gm]).map(managerShort).join(', ')}
          {p.isRevote && <span className="mut"> · mandatory re-vote</span>}
          {parentNumber && <span className="mut"> · branched from item {parentNumber}</span>}
        </span>
        <select className="kindpick" value={e.kind}
          aria-label="Effect kind"
          onChange={(ev) => commit({ ...e, kind: ev.target.value as EffectKind })}>
          {KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
        </select>
      </div>

      {e.kind === 'parameter' && (
        <div className="effsets">
          <span className="efflabel">Sets</span>
          {(e.sets ?? []).map((s, i) => (
            <span className="setrow" key={`${s.key}${i}`}>
              <select value={s.key}
                onChange={(ev) => commit({
                  ...e,
                  sets: (e.sets ?? []).map((x, j) => (j === i
                    ? { ...x, key: ev.target.value as SettingKey } : x)),
                })}>
                {KEYS.map((k) => <option key={k} value={k}>{SETTING_LABEL[k]}</option>)}
              </select>
              <b className="from">{currentOf(s.key)}</b>
              <span className="arrow" aria-hidden="true">→</span>
              <input type="number" className="to" value={s.to}
                aria-label={`${SETTING_LABEL[s.key]} becomes`}
                onChange={(ev) => commit({
                  ...e,
                  sets: (e.sets ?? []).map((x, j) => (j === i
                    ? { ...x, to: Number(ev.target.value) } : x)),
                })} />
              <button type="button" className="linkbtn tiny"
                onClick={() => commit({ ...e, sets: (e.sets ?? []).filter((_, j) => j !== i) })}>
                remove
              </button>
            </span>
          ))}
          <button type="button" className="linkbtn tiny" onClick={() => commit({
            ...e, sets: [...(e.sets ?? []), { key: 'freeKeepers', to: LEAGUE_2026.freeKeepers }],
          })}>add a setting</button>
        </div>
      )}

      {e.kind === 'build' && (
        <>
          <div className="effsets">
            <span className="efflabel">Changes</span>
            <textarea rows={2} value={e.changes ?? ''}
              placeholder="What it does, in your words — and which part is not a parameter."
              onChange={(ev) => commit({ ...e, changes: ev.target.value })} />
          </div>
          {/*
            BEFORE IT CAN APPLY. Passing the rule records it and opens these; it does not silently
            change the app. The list is the difference between "the league voted for this" and
            "the app does this", and the gap between those two is the failure nobody notices until
            a draft is already running.
          */}
          <div className="efftasks">
            <span className="efflabel">Before it can apply</span>
            {(e.tasks ?? []).map((t, i) => (
              <label className="check" key={t.id}>
                <input type="checkbox" checked={Boolean(t.done)}
                  onChange={() => commit({
                    ...e,
                    tasks: (e.tasks ?? []).map((x, j) => (j === i ? { ...x, done: !x.done } : x)),
                  })} />
                <input className="tasklabel" value={t.label}
                  aria-label="What has to be built"
                  onChange={(ev) => commit({
                    ...e,
                    tasks: (e.tasks ?? []).map((x, j) => (j === i
                      ? { ...x, label: ev.target.value } : x)),
                  })} />
              </label>
            ))}
            <button type="button" className="linkbtn tiny" onClick={() => commit({
              ...e,
              tasks: [...(e.tasks ?? []), { id: `t${(e.tasks ?? []).length + 1}`, label: '' }],
            })}>add something</button>
            <p className="note">
              Passing it records the rule and opens these. It does not silently change the app.
            </p>
          </div>
        </>
      )}

      {e.kind === 'table' && (
        <div className="effsets">
          <span className="efflabel">Table</span>
          <select value={e.table?.name ?? 'grid'}
            onChange={(ev) => commit({
              ...e,
              table: { name: ev.target.value as 'grid' | 'luxuryTax' | 'lineup',
                note: e.table?.note ?? '' },
            })}>
            <option value="grid">The Grid</option>
            <option value="luxuryTax">The tax curve</option>
            <option value="lineup">Lineup slots</option>
          </select>
          <input value={e.table?.note ?? ''} placeholder="What the diff does, in a sentence"
            onChange={(ev) => commit({
              ...e, table: { name: e.table?.name ?? 'grid', note: ev.target.value },
            })} />
        </div>
      )}

      {e.kind === 'records' && (
        <p className="note">
          Nothing for the app to do. It joins the rulebook and becomes citable.
        </p>
      )}

      {e.kind !== 'records' && (
        <div className="effwhen">
          <span className="efflabel">Effective</span>
          <select value={e.season}
            aria-label="Effective season"
            onChange={(ev) => commit({ ...e, season: Number(ev.target.value) })}>
            {options.map((o) => (
              <option key={o.season} value={o.season} disabled={!o.ok}>
                {o.season} season{o.ok ? '' : ' — not available'}
              </option>
            ))}
          </select>
          <span className="mut why">{options.find((o) => o.season === e.season)?.why}</span>
        </div>
      )}

      {knocks.length > 0 && (
        <div className="effknock">
          <span className="efflabel">Knock-on</span>
          <span>{knocks.join(' ')}</span>
        </div>
      )}

      {/*
        What happens if it only gets six. The threshold note says it before the vote; here it says
        what that MEANS for the change — written for one season and queued for re-vote, and a
        failed re-vote reverts it.
      */}
      {!p.isRevote && e.kind !== 'records' && (
        <div className="effprov">
          <span className="efflabel">If it passes at 6</span>
          <span>
            Written for {e.season} only and queued for re-vote. A failed re-vote reverts it.{' '}
            <Rule id="2019-I-2">§</Rule>
          </span>
        </div>
      )}
      {p.isRevote && <p className="note">{thresholdNote(p)}</p>}

      <div className="actions">
        <button type="button" className="linkbtn tiny"
          onClick={() => { setDraft(null); setOpen(false); onSave(undefined); }}>
          clear the declaration
        </button>
      </div>
    </li>
  );
}

const currentOf = (k: SettingKey): number => {
  const c = LEAGUE_2026 as unknown as Record<string, number>;
  // Farm picks per year is a farm rule rather than a config field; 1 is what 2025 ran.
  return k in c ? c[k]! : 1;
};
